import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import { yearsAndMonths } from "../../utils";
import DefaultPrimaryMetric from "../metrics/DefaultPrimaryMetric";
import DefaultSecondaryMetric from "../metrics/DefaultSecondaryMetric";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function AcrInsights({ model }: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  // Get common values from first simulation (all simulations share these)
  const firstSim = model.results.simulations[0];

  // Find min/max monthly budgets
  const maxBudget = Math.max(
    ...model.results.simulations.map((sim) => sim.maxAffordable),
  );
  const minBudget = Math.min(
    ...model.results.simulations.map((sim) => sim.maxAffordable),
  );

  // Find cheapest simulation
  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      return sim.totalCostOfMortgage < cheapest.totalCostOfMortgage
        ? sim
        : cheapest;
    },
    model.results.simulations[0],
  );

  // Calculate metrics for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const monthlyBudget = sim.maxAffordable;
    const monthlyContribution =
      monthlyBudget - sim.mortgage.desiredMonthlyPayment;
    const totalAcr = sim.mortgage.ledger.entries.reduce(
      (sum, entry) => sum + entry.additionalCapitalRepayments,
      0,
    );
    const monthsSaved = sim.monthsPaidOffSooner ?? 0;
    const interestSaved =
      model.results.simulations[model.results.simulations.length - 1]
        .totalInterestPaid - sim.totalInterestPaid;
    const savingsPercentage =
      sim.totalInterestEarned > 0
        ? ((sim.totalInterestEarned / sim.totalInterestPaid) * 100).toFixed(1)
        : null;

    return {
      sim,
      monthlyBudget,
      monthlyContribution,
      totalAcr,
      monthsSaved,
      interestSaved,
      savingsPercentage,
    };
  });

  // Sort by monthly budget (lowest first)
  const sortedByBudget = [...simulationsWithMetrics].sort(
    (a, b) => a.monthlyBudget - b.monthlyBudget,
  );

  const lowestBudget = sortedByBudget[0];
  const highestBudget = sortedByBudget[sortedByBudget.length - 1];

  // Calculate savings from highest budget vs lowest
  const interestSavingsFromHigherBudget =
    lowestBudget.sim.totalInterestPaid - highestBudget.sim.totalInterestPaid;
  const monthsSavedFromHigherBudget =
    lowestBudget.sim.numberOfMonths - highestBudget.sim.numberOfMonths;

  return (
    <ModeInsightsCard>
      {/* Header showing common loan details */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          <DefaultPrimaryMetric sim={firstSim} />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <DefaultSecondaryMetric sim={firstSim} />
        </Typography>
      </Box>

      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with monthly
        budgets ranging from <strong>{c(minBudget, true)}</strong> to{" "}
        <strong>{c(maxBudget, true)}</strong>.
      </Typography>

      <Typography variant="body2" gutterBottom>
        The most cost-effective option was a monthly budget of{" "}
        <strong>{c(cheapestSimulation.maxAffordable, true)}</strong> with a
        total cost of{" "}
        <strong>{c(cheapestSimulation.totalCostOfMortgage, true)}</strong>.
      </Typography>

      {interestSavingsFromHigherBudget > 0 && (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Increasing the monthly budget from{" "}
          <strong>{c(lowestBudget.monthlyBudget, true)}</strong> to{" "}
          <strong>{c(highestBudget.monthlyBudget, true)}</strong> saves{" "}
          <strong>{c(interestSavingsFromHigherBudget, true)}</strong> in
          interest and pays off the mortgage{" "}
          <strong>{yearsAndMonths(monthsSavedFromHigherBudget)}</strong> sooner.
        </Typography>
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, mb: 2, fontStyle: "italic" }}
      >
        Each month, the difference between the monthly budget and the mortgage
        payment is added to a reserve account. When conditions allow, funds from
        this reserve are used to make additional capital repayments, reducing
        the outstanding balance faster.
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Monthly Budget</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Amount added to the reserve account each month (budget minus mortgage payment)">
                  <strong>Monthly Contribution</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Total additional capital repayments made over the life of the mortgage">
                  <strong>Total ACR</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Time saved compared to paying the minimum required amount">
                  <strong>Time Saved</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {model.results.simulations.some(
                (sim) => sim.totalInterestEarned > 0,
              ) && (
                <TableCell align="right">
                  <Tooltip title="% of mortgage interest offset by savings & investments interest">
                    <strong>Savings Offset</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByBudget.map(
              ({
                sim,
                monthlyBudget,
                monthlyContribution,
                totalAcr,
                monthsSaved,
                savingsPercentage,
              }) => (
                <TableRow
                  key={monthlyBudget}
                  sx={{
                    backgroundColor:
                      sim === cheapestSimulation
                        ? "success.lighter"
                        : sim === lowestBudget.sim
                          ? "error.lighter"
                          : "inherit",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {c(monthlyBudget, true)}
                    {sim === cheapestSimulation && <StarIcon />}
                  </TableCell>
                  <TableCell align="right">
                    {c(monthlyContribution, true)}
                  </TableCell>
                  <TableCell align="right">{c(totalAcr, true)}</TableCell>
                  <TableCell align="right">
                    {monthsSaved > 0 ? yearsAndMonths(monthsSaved) : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalCostOfMortgage, true)}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalInterestPaid, true)}
                  </TableCell>
                  {model.results.simulations.some(
                    (s) => s.totalInterestEarned > 0,
                  ) && (
                    <TableCell align="right">
                      {savingsPercentage ? `${savingsPercentage}%` : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, fontSize: "0.75rem" }}
      >
        <strong>Note:</strong> The "Time Saved" column shows how much sooner the
        mortgage is paid off compared to the original term. Additional capital
        repayments are subject to lender limits (typically 10% of the
        outstanding balance per year during a fixed deal).
      </Typography>
    </ModeInsightsCard>
  );
}
